import { useState } from 'react'
import { FiLock, FiX, FiAlertCircle } from 'react-icons/fi'
import toast from 'react-hot-toast'
import { authAPI } from '../../services/api'
import { Btn } from '../../ui'

/*
 * ModalResetPasswordAdmin (V2) — DS v2.
 * Reset administrativo de contraseña: el admin (o super admin) define una
 * nueva contraseña para el empleado SIN necesidad de la actual.
 *
 * Permisos verificados en backend (POST /api/auth/admin/change-password):
 *   - admin → solo empleados de su empresa
 *   - super_admin → cualquier empleado
 *
 * Reusable desde GestionEmpleados (admin) y GestionUsuariosGlobal (super admin).
 */

const ModalResetPasswordAdmin = ({ empleado, onClose, onSuccess }) => {
  const [form, setForm] = useState({ password: '', passwordConfirm: '' })
  const [guardando, setGuardando] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (form.password.length < 8) {
      toast.error('La contraseña debe tener al menos 8 caracteres')
      return
    }
    if (form.password !== form.passwordConfirm) {
      toast.error('Las contraseñas no coinciden')
      return
    }

    setGuardando(true)
    try {
      await authAPI.adminChangePassword(empleado.id, form.password)
      toast.success(`Contraseña de ${empleado.nombre} actualizada`)
      onSuccess?.()
      onClose()
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al resetear la contraseña')
    } finally {
      setGuardando(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="ui-card w-full max-w-md">
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-[var(--line)]">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-soft bg-[rgba(245,158,11,.10)] flex items-center justify-center">
              <FiLock size={15} className="text-[#A86A06]" />
            </div>
            <div>
              <div className="eyebrow mb-1">Reset administrativo</div>
              <h3 className="font-display text-[20px] text-[var(--ink)] leading-tight">Nueva contraseña.</h3>
              <p className="text-[12px] text-[var(--ink-3)] mt-0.5">
                Para <strong className="text-[var(--ink-2)]">{empleado.nombre}</strong>
                {empleado.email && <> · <span className="font-mono">{empleado.email}</span></>}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-soft text-[var(--ink-3)] hover:text-[var(--ink)] hover:bg-[var(--bg-2)] transition-colors"
            aria-label="Cerrar"
            disabled={guardando}
          >
            <FiX size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="eyebrow block mb-1.5">Nueva contraseña *</label>
            <input
              type="password"
              value={form.password}
              onChange={e => setForm({ ...form, password: e.target.value })}
              className="w-full px-3 py-2 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft text-[14px] focus:outline-none focus:border-[var(--ink-3)]"
              required
              minLength={8}
              placeholder="Mínimo 8 caracteres"
              autoFocus
            />
          </div>

          <div>
            <label className="eyebrow block mb-1.5">Confirmar contraseña *</label>
            <input
              type="password"
              value={form.passwordConfirm}
              onChange={e => setForm({ ...form, passwordConfirm: e.target.value })}
              className={`w-full px-3 py-2 bg-[var(--bg-2)] border rounded-soft text-[14px] focus:outline-none ${
                form.passwordConfirm && form.password !== form.passwordConfirm
                  ? 'border-[#C13030] focus:border-[#C13030]'
                  : 'border-[var(--line)] focus:border-[var(--ink-3)]'
              }`}
              required
              minLength={8}
            />
            {form.passwordConfirm && form.password !== form.passwordConfirm && (
              <p className="text-[11px] text-[#C13030] mt-1">Las contraseñas no coinciden</p>
            )}
          </div>

          <div className="flex items-start gap-2 p-3 rounded-soft bg-[rgba(245,158,11,.06)] border border-[rgba(245,158,11,.20)]">
            <FiAlertCircle size={14} className="text-[#A86A06] mt-0.5 flex-shrink-0" />
            <p className="text-[11.5px] text-[#A86A06]">
              El empleado tendrá que entrar con esta contraseña en su próximo acceso.
              Comunícasela por un canal seguro y pídele que la cambie cuando entre.
            </p>
          </div>

          <div className="flex gap-3 pt-1">
            <Btn type="button" variant="ghost" className="flex-1" onClick={onClose} disabled={guardando}>
              Cancelar
            </Btn>
            <Btn type="submit" variant="primary" className="flex-1" disabled={guardando}>
              {guardando ? 'Guardando…' : 'Resetear contraseña'}
            </Btn>
          </div>
        </form>
      </div>
    </div>
  )
}

export default ModalResetPasswordAdmin
